import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'config/axiosInstance';
import monthlyinspection from '../../assets/icons/driverDetail/monthlysubmissions.svg';
import failedinspection from '../../assets/icons/driverDetail/exceedalcohollimit.svg';
import FailedInspection from 'reusable/FailedInspection';
import DateRangeFilter from 'reusable/datefilterrange';
import Pagination from 'reusable/pagination';
import { PAGE_SIZE } from 'config/constant';
import false_icon from '../../assets/icons/false.svg';
import true_icon from '../../assets/icons/true.svg';
import { useTranslation } from 'react-i18next';

interface VehicleProfileProps {}

interface InspectionRecord {
  id: string;
  create_time: string;
  vehicle: { plate_number: string };
  details: { vehicle_part: VehiclePart[] };
  user: { name: string };
}

interface VehiclePart {
  name: string;
  item: { status: boolean }[];
}

const VehicleProfile: React.FC<VehicleProfileProps> = () => {
  const { t, i18n } = useTranslation('vehicle');
  const { id } = useParams<{ id: string }>();
  const [vehicleProfile, setVehicleProfile] = useState<InspectionRecord[]>([]);
  const [platnumber, setPlatenumber] = useState('unknown');
  const [vehicleModel, setVehicleModel] = useState('unknown');
  const [monthlyInpection, setMonthlyInspection] = useState(0);
  const [totalInpection, setTotalInspection] = useState(0);
  const [monthlyFailedInpection, setMonthlyFailedInspection] = useState(0);
  const [totalFailedInpection, setTotalFailedInspection] = useState(0);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [failedInspection, setFailedInspection] = useState<boolean | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const startMonth = '2024-07-01';
  const endMonth = '2024-07-31';

  const vehicleParts = [
    { en: 'Engine', jp: 'エンジン' },
    { en: 'Lighting/Blinker', jp: '照明装置/方向指示器' },
    { en: 'Tire', jp: 'タイヤ' },
    { en: 'Battery', jp: 'バッテリー' },
    { en: 'Air Tank', jp: 'エアタンク' },
    { en: 'Brake', jp: 'ブレーキ' },
    { en: 'Window Washer Liquid', jp: 'ウインドウォッシャー液' },
    { en: 'Wiper', jp: 'ワイパー' },
  ];

  const handleDateRangeChange = (startDate: string, endDate: string) =>
    setDateRange({ startDate, endDate });
  const handleFailedInspectionChange = (value: boolean | null) =>
    setFailedInspection(value);
  const handlePageChange = (value: number) => setCurrentPage(value);

  useEffect(() => {
    const fetchVehicleProfile = async () => {
      try {
        const [
          { data: recordsData },
          { data: vehicleData },
          { data: totalInspData },
          { data: monthlyInspData },
          { data: totalFailData },
          { data: monthlyFailData },
        ] = await Promise.all([
          axiosInstance.get(`/v1/vehicle_inspection_records/?vehicle=${id}&inspecion_result=${failedInspection}`),
          axiosInstance.get(`/v1/vehicles/${id}/`),
          axiosInstance.get(`/v1/vehicle_inspection_records/?vehicle=${id}`),
          axiosInstance.get(
            `/v1/vehicle_inspection_records/?create_time__gte=${startMonth}&create_time__lte=${endMonth}&vehicle=${id}`
          ),
          axiosInstance.get(
            `/v1/vehicle_inspection_records/?vehicle=${id}&inspecion_result=false`
          ),
          axiosInstance.get(
            `/v1/vehicle_inspection_records/?create_time__gte=${startMonth}&create_time__lte=${endMonth}&inspecion_result=false&vehicle=${id}`
          ),
        ]);

        setVehicleProfile(recordsData.results);
        setCount(recordsData.count);
        setPlatenumber(vehicleData.plate_number);
        setVehicleModel(vehicleData.car_model);
        setTotalInspection(totalInspData.count);
        setMonthlyInspection(monthlyInspData.count);
        setTotalFailedInspection(totalFailData.count);
        setMonthlyFailedInspection(monthlyFailData.count);
      } catch (error) {
        console.error('Failed to fetch vehicle profile', error);
      }
    };

    fetchVehicleProfile();
  }, [id, failedInspection]);

  const handleRowClick = (recordId: string) => navigate(`/vehicle/${recordId}`);

  const checkStatus = (record: InspectionRecord, partName: string) => {
    const part = record.details?.vehicle_part?.find(
      (part) => part.name === partName
    );
    if (!part || part.item.length === 0) return null; // Return null if part or item is not found
    return part.item.every((item) => item.status === true);
  };

  const checkPartStatus = (
    record: InspectionRecord,
    part: { en: string; jp: string }
  ) => checkStatus(record, part.en) ?? checkStatus(record, part.jp);
console.log(failedInspection);
  return (
    <section className="flex flex-col gap-5 p-4">
      <div>
        <h4 className="text-title font-medium">
          {t('all-vehicle.vehicle-profile.title')}
        </h4>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-col justify-center w-1/3 rounded-md bg-white p-4">
          <p className="text-title font-medium">{platnumber}</p>
          <p className="text-gray-400">{vehicleModel}</p>
        </div>
        <div className="flex w-2/3 flex-row rounded-md bg-white">
          <div className="flex-1 p-4 border-r">
            <div className="flex flex-row justify-between">
              <p className="text-title font-medium">{monthlyInpection}</p>
              <img
                src={monthlyinspection}
                alt="monthly inspection"
                className="bg-primary/50 p-2 rounded-full"
              />
            </div>
            <div className="flex flex-col">
              <p>
                {t('all-vehicle.vehicle-profile.status.monthly-inspection')}
              </p>
              <p className="text-gray-400">
                {totalInpection}{' '}
                {t(
                  'all-vehicle.vehicle-profile.status.monthly-inspection-info'
                )}
              </p>
            </div>
          </div>
          <div className="flex-1 p-4">
            <div className="flex flex-row justify-between">
              <p className="text-title font-medium">{monthlyFailedInpection}</p>
              <img
                src={failedinspection}
                alt="monthly failed inspection"
                className="bg-primary/50 p-2 rounded-full"
              />
            </div>
            <div>
              <p>{t('all-vehicle.vehicle-profile.status.failed-inspection')}</p>
              <p className="text-gray-400">
                {totalFailedInpection}{' '}
                {t('all-vehicle.vehicle-profile.status.failed-inspection-info')}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col bg-white rounded-md p-4 gap-4">
        <div className="flex flex-row justify-between">
          <h4 className="text-subtitle-1 font-medium">
            {t('all-vehicle.vehicle-profile.table.title')}
          </h4>
          <div className="flex flex-row gap-4 items-center">
            <DateRangeFilter
              onDateRangeChange={handleDateRangeChange}
              position="right"
            />
            <FailedInspection onChange={handleFailedInspectionChange} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4 items-center">
            <h6 className="w-2/12">
              {t('all-vehicle.vehicle-profile.table.inspection-date')}
            </h6>
            <h6 className="w-1/12">
              {t('all-vehicle.vehicle-profile.table.plate-number')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.engine')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.lighting')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.tire')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.battery')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.air-tank')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.brake')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.window-washer-liquid')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.wiper')}
            </h6>
            <h6 className="w-2/12 text-center">
              {t('all-vehicle.vehicle-profile.table.inspection-by')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.details')}
            </h6>
          </div>
          <div className="px-4">
            {vehicleProfile.map((record) => (
              <div
                key={record.id}
                className="flex flex-row items-center justify-between border-b cursor-pointer"
                onClick={() => handleRowClick(record.id)}
              >
                <p className="w-2/12 py-3">
                  {record.create_time.substring(0, 10)}
                </p>
                <p className="w-1/12 text-left">
                  {record.vehicle.plate_number}
                </p>
                {vehicleParts.map((part) => {
                  const status = checkPartStatus(record, part);
                  return (
                    <p key={part.en} className="flex w-1/12 justify-center">
                      {status === null || status === undefined ? (
                        '-' // Display "-" if no value is present
                      ) : (
                        <img
                          className="h-7"
                          src={status ? true_icon : false_icon}
                          alt={status ? 'True' : 'False'}
                        />
                      )}
                    </p>
                  );
                })}
                <p className="w-2/12 text-center">{record.user.name}</p>
                <p className="w-1/12 text-center underline text-blue-600">
                  {t('all-vehicle.vehicle-profile.table.detail_button')}
                </p>
              </div>
            ))}
          </div>
          <Pagination
            allRows={count}
            rowsPerPage={PAGE_SIZE}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </section>
  );
};

export default VehicleProfile;